import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoanApplicationService } from '../loan-applications/loan-applications.service';
import { UserKYCService } from '../kyc/userkyc.service';
import { ProductsService } from '../Interface & Service/product/products.service';
import { ApplicationDetailsService } from '../loan-applications/loan-application-details/loan-application-details.service';
import { LoanApplicationItem } from '../loan-applications/loan-applications.interface';
import { UserKYCDTO } from '../kyc/userkyc.interface';
import { ProductDTO } from '../Interface & Service/product/product.interface';
import { ApplicationDetailsDTO } from '../loan-applications/loan-application-details/loan-application-details.interface';

/**
 * LosStateService — provided at LoanApplicationMasterComponent level (NOT root).
 * Acts as a reactive state bus for the entire LOS stepper flow.
 * Steps subscribe to observables and call refresh* methods after completing a step.
 */
@Injectable()
export class LosStateService {
  private loanAppService = inject(LoanApplicationService);
  private userKYCService = inject(UserKYCService);
  private productsService = inject(ProductsService);
  private detailsService = inject(ApplicationDetailsService);

  private _appData$     = new BehaviorSubject<LoanApplicationItem | null>(null);
  private _kycData$     = new BehaviorSubject<UserKYCDTO | null>(null);
  private _productData$ = new BehaviorSubject<ProductDTO | null>(null);
  private _details$     = new BehaviorSubject<ApplicationDetailsDTO[]>([]);
  private _loading$     = new BehaviorSubject<boolean>(false);

  readonly applicationData$ = this._appData$.asObservable();
  readonly userKycData$     = this._kycData$.asObservable();
  readonly productData$     = this._productData$.asObservable();
  readonly applicationDetails$ = this._details$.asObservable();
  readonly loading$          = this._loading$.asObservable();

  get applicationSnapshot(): LoanApplicationItem | null { return this._appData$.getValue(); }
  get kycSnapshot(): UserKYCDTO | null                  { return this._kycData$.getValue(); }
  get productSnapshot(): ProductDTO | null               { return this._productData$.getValue(); }
  get detailsSnapshot(): ApplicationDetailsDTO[]        { return this._details$.getValue(); }

  /** Full initialisation — fetches app, product and KYC in sequence. */
  initialize(applicationId: number): void {
    this._loading$.next(true);
    this.loanAppService.LoanApplication_ReadById({ applicationId }).subscribe({
      next: (app) => {
        this._appData$.next(app);
        if (app?.productCode) {
          this.productsService.Product_ReadByProductCode({ productCode: app.productCode }).subscribe({
            next: (prod) => this._productData$.next(prod),
            error: () => {}
          });
        }
        
        // Load application details
        this.detailsService.ApplicationDetails_ReadByApplicationId({ applicationId }).subscribe({
          next: (res) => {
            if (res && res.items) {
              this._details$.next(res.items);
            }
          },
          error: (err) => console.error('[LosState] details read error:', err)
        });

        if (app?.userId) {
          this.userKYCService.UserKYC_ReadByUserId({ userId: app.userId }).subscribe({
            next:  (kyc) => { this._kycData$.next(kyc); this._loading$.next(false); },
            error: ()    => { this._loading$.next(false); }
          });
        } else {
          this._loading$.next(false);
        }
      },
      error: (err) => {
        console.error('[LosState] initialize error:', err);
        this._loading$.next(false);
      }
    });
  }

  /** Re-fetch application record only (call after each step update). */
  refreshApplication(applicationId: number): void {
    this.loanAppService.LoanApplication_ReadById({ applicationId }).subscribe({
      next:  (app) => this._appData$.next(app),
      error: (err) => console.error('[LosState] refreshApplication error:', err)
    });
  }

  /** Re-fetch KYC record only (call after KYC / financial steps). */
  refreshKyc(userId: number): void {
    this.userKYCService.UserKYC_ReadByUserId({ userId }).subscribe({
      next:  (kyc) => this._kycData$.next(kyc),
      error: (err) => console.error('[LosState] refreshKyc error:', err)
    });
  }

  /** Re-fetch application details only. */
  refreshDetails(applicationId: number): void {
    this.detailsService.ApplicationDetails_ReadByApplicationId({ applicationId }).subscribe({
      next: (res) => {
        if (res && res.items) {
          this._details$.next(res.items);
        }
      },
      error: (err) => console.error('[LosState] refreshDetails error:', err)
    });
  }

  /** Optimistic patch — avoids an extra round-trip when the caller has the fresh object. */
  patchApplication(data: LoanApplicationItem): void { this._appData$.next(data); }
  patchKyc(data: UserKYCDTO): void                  { this._kycData$.next(data); }
}
